// Common sections helpers
import { ComplexCommission, ComplexCommissionTier, CommissionPayment, DefaultInput } from "@PayPlans/helpers/DefaultSections/Commons";

// Interfaces
import { ICommissionPaymentProps, IDefaultAdvisor, IDefaultAdvisorDefinition } from "./interfaces";
import { TTierBonus } from "@/PayPlans/components/Commons/ElrHroExtension/interfaces";

const tierCommissionsPayment = (payType = "Customer", args: ICommissionPaymentProps | undefined = undefined) => {
	const MAX_INPUT_VALUE = 1000000;
	const { fromDefinition, toDefinition, commissionDefinition } = args ?? {};
	const { value: fromValue = -MAX_INPUT_VALUE, input: fromInput = "Dollar" } = fromDefinition || {};
	const { value: toValue = MAX_INPUT_VALUE, input: toInput = "Dollar" } = toDefinition || {};
	return {
		from: DefaultInput(Number(fromValue), fromInput),
		to: DefaultInput(Number(toValue), toInput),
		commissions: [CommissionPayment(payType, { commissionDefinition })],
	};
};

const defaultAdvisorDefinition = (payType = "Customer"): IDefaultAdvisorDefinition => ({
	tiers: [tierCommissionsPayment(payType)],
});

const defaultAdvisors = (defaultPayment = "Customer"): IDefaultAdvisor => {
	return {
		parts: defaultAdvisorDefinition(defaultPayment),
		labor: defaultAdvisorDefinition(defaultPayment),
		partsAndLabor: defaultAdvisorDefinition(defaultPayment),
		isPartsAndLabor: false,
		isGuaranteedDraw: false,
	};
};

const followUpPerformanceBonusDefault = () => {
	const max = 1000;
	const maxDuration = 2208;
	const commission = ComplexCommission([maxDuration, "Duration"], [0, "Duration"], {
		identifier: "bonus",
		commission: [0, "Percent"],
	});
	return {
		tiers: [ComplexCommissionTier([-max, "Unit"], [max, "Unit"], { commissions: [commission], identifier: "bonuses" })],
		applyTiersRetroactively: false,
	};
};

const MIN_INITIAL_GROSS = -10000;
const MAX_INITIAL_GROSS = 1000000;

function getInitialTiers(variant: "ELR" | "HRO"): TTierBonus[] {
	const isHRO = variant === "HRO";
	const fromToInput = isHRO ? "Unit" : "Dollar";
	const fromMin = isHRO ? 0 : MIN_INITIAL_GROSS;
	const toMax = isHRO ? 1000 : MAX_INITIAL_GROSS;
	return [
		{
			from: { value: fromMin, input: fromToInput },
			to: { value: toMax, input: fromToInput },
			bonuses: [
				{
					from: { value: MIN_INITIAL_GROSS, input: "Dollar" },
					to: { value: MAX_INITIAL_GROSS, input: "Dollar" },
					bonus: { value: 0, input: "Dollar" },
				},
			],
		},
	];
}

const defaultELRBonusTabContent = {
	global: false,
	option: "gross",
	tiers: getInitialTiers("ELR"),
};

const defaultHROBonusTabContent = {
	global: false,
	option: "gross",
	tiers: getInitialTiers("HRO"),
};

function defaultELRBonus() {
	return {
		customer: { ...defaultELRBonusTabContent },
		internal: { ...defaultELRBonusTabContent },
		warranty: { ...defaultELRBonusTabContent },
	};
}

function defaultHROBonus() {
	return {
		customer: { ...defaultHROBonusTabContent },
		internal: { ...defaultHROBonusTabContent },
		warranty: { ...defaultHROBonusTabContent },
	};
}

const defaultRetentionPayContent = { value: 0, input: "Percent" };

function defaultRetentionPay() {
	return {
		retention: { ...defaultRetentionPayContent },
		internal: { ...defaultRetentionPayContent },
		warranty: { ...defaultRetentionPayContent },
	};
}

export {
	defaultAdvisors as DefaultAdvisors,
	defaultAdvisorDefinition as DefaultAdvisorDefinition,
	tierCommissionsPayment as TierCommissionsPayment,
	followUpPerformanceBonusDefault as FollowUpPerformanceBonusDefault,
	defaultRetentionPay as DefaultRetentionPay,
	defaultELRBonus as DefaultELRBonus,
	defaultHROBonus as DefaultHROBonus,
};
